import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useDispatch } from 'react-redux';
import { fetchApi, fetchWithError } from '../react-query/fetchWithError';
import { queryKeys } from '../react-query/queryKeys';
import { 
  ocpiTokenUrl, 
  partnerTokenUrl,
  partnersUrl,
  partnerUrl,
} from '../react-query/endPoints';
import { alertSlice } from '../store/alert/alert.slice';

export const useOcpiToken = (countryCode, partyId, uid, type) => {
  return useQuery({
    queryKey: queryKeys.ocpiToken(countryCode, partyId, uid, type),
    queryFn: fetchWithError,
    enabled: !!(countryCode && partyId && uid && type),
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });
};

export const useUpdateToken = () => {
  const queryClient = useQueryClient();
  const dispatch = useDispatch();
  
  return useMutation({
    mutationFn: (tokenData) => 
      fetchApi({
        url: partnerTokenUrl,
        method: 'PUT',
        body: tokenData,
      }),
    onSuccess: (data) => {
      dispatch(
        alertSlice.actions.setAlert({
          open: true,
          message: 'Token updated successfully',
          severity: 'success',
        })
      );
      // Invalidate and refetch tokens
      queryClient.invalidateQueries({ queryKey: ['partnerGetAllTokens'] });
    },
    onError: (error) => {
      const message = error?.response?.data?.message || error?.message || 'Failed to update token';
      console.log(message);
      dispatch(
        alertSlice.actions.setAlert({
          open: true,
          message,
          severity: 'error',
        })
      );
    },
  });
};

export const usePatchToken = () => {
  const queryClient = useQueryClient();
  const dispatch = useDispatch();
  
  return useMutation({
    mutationFn: (tokenData) => 
      fetchApi( {
        url: partnerTokenUrl,
        method: 'PATCH',
        body: tokenData,
      }),
    onSuccess: (data) => {
      dispatch(
        alertSlice.actions.setAlert({
          open: true,
          message: 'Token updated successfully',
          severity: 'success',
        })
      );
      // Invalidate and refetch tokens
      queryClient.invalidateQueries({ queryKey: ['partnerGetAllTokens'] });
    },
    onError: (error) => {
      const message = error?.response?.data?.message || error?.message || 'Failed to update token';
      dispatch(
        alertSlice.actions.setAlert({
          open: true,
          message,
          severity: 'error',
        })
      );
    },
  });
};

export const useDeleteToken = () => {
  const queryClient = useQueryClient();
  const dispatch = useDispatch();
  
  return useMutation({
    mutationFn: ({ countryCode, partyId, uid }) => 
      fetchWithError(`${ocpiTokenUrl(countryCode, partyId, uid)}`, {
        method: 'DELETE',
      }),
    onSuccess: (data) => {
      dispatch(
        alertSlice.actions.setAlert({
          open: true,
          message: 'Token deleted successfully',
          severity: 'success',
        })
      );
      // Invalidate and refetch tokens
      queryClient.invalidateQueries({ queryKey: ['partnerGetAllTokens'] });
    },
    onError: (error) => {
      const message = error?.response?.data?.message || error?.message || 'Failed to delete token';
      dispatch(
        alertSlice.actions.setAlert({
          open: true,
          message,
          severity: 'error',
        })
      );
    },
  });
};

export const usePartners = () => {
  return useQuery({
    queryKey: queryKeys.partners(),
    queryFn: () => fetchWithError(partnersUrl),
    staleTime: 10 * 60 * 1000, // 10 minutes
    gcTime: 15 * 60 * 1000, // 15 minutes
  });
};

export const usePartner = (partnerId) => {
  return useQuery({
    queryKey: queryKeys.partner(partnerId),
    queryFn: () => fetchWithError(partnerUrl(partnerId)),
    enabled: !!partnerId,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });
}; 